/**
 * Fusível e trava do sistema de redução de força: o elo "fraco" de propósito, que cede
 * antes de o sistema (ou a ancoragem) ser sobrecarregado, quando a carga trava
 * e a equipe continua puxando.
 *
 *  - Prusik: um laço de cordelete em nó de fricção na corda de içamento. Escorrega
 *    quando a tensão passa do limite e pode ser reajustado (reutilizável).
 *  - Clássico: um cordelete de ruptura conhecida em série com a corda. Rompe e
 *    precisa ser trocado (uso único).
 *  - Freio oito: não cede; é a TRAVA que segura a carga enquanto a equipe volta
 *    para puxar de novo (o freio atrita a corda e a ponta livre segura pouco peso).
 *    Na passagem esportiva a corda dá só uma volta no freio (atrito menor, a
 *    carga escapa se a mão soltar); na passagem travada ela ganha a volta na
 *    orelha, e o atrito sobe a ponto de segurar com uma mão.
 *
 * Os limites são valores didáticos para o instrutor validar, como as demais
 * referências do app; não são os de um cordelete específico. TypeScript puro.
 */

export type IdFusivel = 'nenhum' | 'prusik' | 'classico' | 'freioOitoEsportivo' | 'freioOitoTravado'

export interface TipoDeFusivel {
  readonly id: IdFusivel
  readonly nome: string
  /** Tensão, na corda de içamento, em que o fusível cede (kgf). */
  readonly limiteKgf: number
  /** O que acontece ao ceder. */
  readonly aoCeder: string
  readonly reutilizavel: boolean
  /** Só nos freios: quantas vezes o atrito reduz o esforço para segurar a carga. */
  readonly fatorDeFreio?: number
}

/** O esforço que uma pessoa segura sozinha, com uma mão, na ponta livre do freio (kgf, didático). */
export const ESFORCO_COM_UMA_MAO_KGF = 20

/** Na ordem do parâmetro `fusivel` do sistema (0, 1, 2). */
export const TIPOS_FUSIVEL: readonly TipoDeFusivel[] = [
  { id: 'nenhum', nome: 'Sem fusível', limiteKgf: Number.POSITIVE_INFINITY, aoCeder: '', reutilizavel: true },
  { id: 'prusik', nome: 'Prusik', limiteKgf: 200, aoCeder: 'o nó escorrega pela corda e a carga para de subir', reutilizavel: true },
  { id: 'classico', nome: 'Fusível clássico', limiteKgf: 300, aoCeder: 'o cordelete rompe e o sistema perde a carga', reutilizavel: false },
  { id: 'freioOitoEsportivo', nome: 'Freio oito (passagem esportiva)', limiteKgf: Number.POSITIVE_INFINITY, aoCeder: '', reutilizavel: true, fatorDeFreio: 3 },
  { id: 'freioOitoTravado', nome: 'Freio oito (passagem travada)', limiteKgf: Number.POSITIVE_INFINITY, aoCeder: '', reutilizavel: true, fatorDeFreio: 8 },
]

/** O que uma equipe de duas pessoas consegue puxar na ponta livre, em kgf. */
export const FORCA_DE_PUXADA_MAXIMA_KGF = 100

export const nomesDosFusiveis = TIPOS_FUSIVEL.map((t) => t.nome)

export const tipoDeFusivel = (indice: number | undefined): TipoDeFusivel =>
  TIPOS_FUSIVEL[Math.min(TIPOS_FUSIVEL.length - 1, Math.max(0, Math.round(indice ?? 0)))]!

export interface AnaliseDoFusivel {
  readonly tipo: TipoDeFusivel
  /** Tensão na corda de içamento se a carga travar e a equipe puxar com tudo (kgf). */
  readonly tensaoSeTravarKgf: number
  /** O fusível cede nessa situação? */
  readonly cede: boolean
  /** Tensão que chega à corda e à ancoragem, já limitada pelo fusível (kgf). */
  readonly tensaoLimitadaKgf: number
  /** Força no bloco fixo (a ancoragem) com a carga travada e o fusível em ação (kgf). */
  readonly cargaNaAncoragemSeTravarKgf: number
  /** Só nos freios: esforço na ponta livre do freio para segurar a carga parada (kgf). */
  readonly esforcoParaSegurarKgf: number | null
  /** Só nos freios: uma pessoa segura a carga sozinha, com uma mão? */
  readonly seguraComUmaMao: boolean | null
  /** Texto curto para o painel. */
  readonly resumo: string
}

const kgf = (v: number) => `${Math.round(v)} kgf`

/**
 * O fusível numa carga travada: a equipe puxa `FORCA_DE_PUXADA_MAXIMA_KGF` e o
 * sistema multiplica pela vantagem real. Sem fusível, tudo isso vai para a
 * corda e a ancoragem; com ele, a tensão é limitada ao que o fusível aguenta.
 */
export function analisarFusivel(indice: number | undefined, vantagemReal: number, cargaKgf = 120): AnaliseDoFusivel {
  const tipo = tipoDeFusivel(indice)
  const tensaoSeTravarKgf = FORCA_DE_PUXADA_MAXIMA_KGF * vantagemReal
  const cede = tensaoSeTravarKgf > tipo.limiteKgf
  const tensaoLimitadaKgf = Math.min(tensaoSeTravarKgf, tipo.limiteKgf)
  // a ponta livre sai para o lado oposto da carga: a ancoragem segura a tensão menos a puxada
  const cargaNaAncoragemSeTravarKgf = tensaoLimitadaKgf * (1 - 1 / vantagemReal)
  const esforcoParaSegurarKgf = tipo.fatorDeFreio ? cargaKgf / tipo.fatorDeFreio : null
  const seguraComUmaMao = esforcoParaSegurarKgf === null ? null : esforcoParaSegurarKgf <= ESFORCO_COM_UMA_MAO_KGF
  const resumo =
    esforcoParaSegurarKgf !== null
      ? `trava: segura a carga de ${kgf(cargaKgf)} com ${kgf(esforcoParaSegurarKgf)} na ponta livre do freio (atrito ${tipo.fatorDeFreio}:1) — ${
          seguraComUmaMao ? 'uma pessoa segura com uma mão' : `passa de ${kgf(ESFORCO_COM_UMA_MAO_KGF)}: precisa de duas mãos ou de um segundo operador`
        }. Não protege de sobrecarga: com a carga travada, ${kgf(tensaoSeTravarKgf)} na corda`
      : tipo.id === 'nenhum'
      ? `carga travada: ${kgf(tensaoSeTravarKgf)} na corda e ${kgf(cargaNaAncoragemSeTravarKgf)} na ancoragem, sem nada que ceda`
      : cede
        ? `cede a ${kgf(tipo.limiteKgf)} (travada: ${kgf(tensaoSeTravarKgf)}): ${tipo.aoCeder}`
        : `não cede (travada: ${kgf(tensaoSeTravarKgf)}, limite ${kgf(tipo.limiteKgf)})`
  return { tipo, tensaoSeTravarKgf, cede, tensaoLimitadaKgf, cargaNaAncoragemSeTravarKgf, esforcoParaSegurarKgf, seguraComUmaMao, resumo }
}
